import { AlertColor, Box, Tab, Tabs } from '@mui/material';
import PageLoader from 'components/PageLoader';
import { useCallback, useEffect, useState, type SyntheticEvent } from 'react';
import { useParams } from 'react-router-dom';
import strings from '../../../../common/Translation/Translate';
import axiosInstance from '../../../../utils/axios';
import { URLS } from '../../../../utils/constants/urls';
import { useSelector } from 'react-redux';
import { privileges } from 'utils/constants/privileges';
import { Privilege } from 'utils/redux/reducer/authentication-slice';
import Notification from 'components/Notification';
import { createDropdownData } from '../../../../utils/validators/HelperFunctions';
import BookingComments from './BookingComments';
import BookingFollowUps from './BookingFollowUps';
import { fetchFromStorage, saveToStorage } from '../../../../utils/storage';
import { TabPanel } from '../../../../utils/validators/HelperComponents';

interface BookingLogsProps {
  bookingLineItem: any;
}

export const BookingLogs = ({ bookingLineItem }: BookingLogsProps) => {
  const { id } = useParams() as any;
  const isPrivilege = useSelector(Privilege);

  const [value, setValue] = useState(parseInt(fetchFromStorage('orgTabValue') ?? '0'));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [assigneeList, setAssigneeList] = useState<any>([]);
  const [followUpModes, setFollowUpModes] = useState<any>([]);
  const [bookingLogs, setBookingLogs] = useState<any>([]);
  const [comments, setComments] = useState<any>([]);
  const [restrictPriceOverride, setRestrictPriceOverride] = useState<boolean>(false);
  const [notification, setNotification] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const handleChange = (_event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
    saveToStorage('orgTabValue', newValue.toString());
  };

  const closeNotification = (open: boolean) => setNotification(prev => ({ ...prev, display: open }));

  const handleCatch = (error: any) => {
    setNotification({
      display: true,
      severity: 'error',
      message: error.response?.data?.error_description ?? error.response?.data?.message ?? error.message,
    });
  };

  const getInitialResponse = useCallback(async () => {
    try {
      setIsLoading(true);
      const { status, data } = await axiosInstance.get(URLS.bookingLogs(id));
      if (status === 200) {
        const customResponse = data?.tasks.map((d: any) => ({
          id: d.id,
          createdBy: d.createdBy,
          createdAt: d.createdAt,
          datetime: d.dueDate,
          mode: d.followUpMode ?? d.mode,
          assignedTo: d.assignedTo ? d.assignedTo.label : null,
          summary: d.description,
          attachmentUrl: d.attachmentUrl,
          fileName: d.fileName,
          fileSize: d.fileSize,
          status: d.status,
          completedAt: d.completedAt,
          completionReason: d.completionReason,
          assignedToId: d?.assignedTo?.value,
        }));
        setBookingLogs(customResponse);
        setComments(data?.comments);
        setRestrictPriceOverride(data?.restrictPriceOverride);
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const getMasterData = useCallback(async () => {
    const { data } = await axiosInstance.get(URLS.followUpDropdown);
    setAssigneeList(createDropdownData(data?.assignees, ['id', 'name']));
    setFollowUpModes(createDropdownData(data?.followUpModes, ['id', 'name']));
  }, []);

  useEffect(() => {
    (async () => {
      if (id) {
        await getInitialResponse();
        await getMasterData();
      }
    })();
  }, [getInitialResponse, getMasterData, id]);

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  return (
    <>
      {notification.display && (
        <Notification
          isOpen={notification.display}
          message={notification.message}
          severity={notification.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}

      {isLoading && <PageLoader />}

      {isPrivilege?.includes(privileges.editBooking) && (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="booking log tabs"
              sx={{
                minHeight: 40,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minHeight: 40,
                  py: 0,
                  fontWeight: 500,
                  fontSize: '0.9rem',
                },
              }}>
              <Tab label={strings.booking_logs_notes} {...a11yProps(0)} />
              <Tab label={strings.booking_logs_follow_ups} {...a11yProps(1)} />
            </Tabs>
          </Box>

          <TabPanel value={value} index={0}>
            <BookingComments bookingLineItemId={id} allComments={comments} getInitialResponse={getInitialResponse} />
          </TabPanel>

          <TabPanel value={value} index={1}>
            <BookingFollowUps
              bookingLineItemId={id}
              bookingLineItem={bookingLineItem}
              followUps={bookingLogs}
              assigneeList={assigneeList}
              followUpModes={followUpModes}
              restrictPriceOverride={restrictPriceOverride}
              getInitialResponse={getInitialResponse}
            />
          </TabPanel>
        </>
      )}
    </>
  );
};
